import { 
  ChartBarSquareIcon,
  CodeBracketIcon,
  CpuChipIcon,
  LockClosedIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  ServerIcon,
  UserGroupIcon
} from '@heroicons/react/20/solid'
import { useTheme } from '../../context/ThemeContext'
import './styles.scss'

const features = [
  {
    name: 'Flexible Time Intervals',
    description:
      'Access market data in multiple timeframes: 1-minute, 30-minute, 1-hour, and daily intervals for comprehensive analysis',
    icon: ChartBarSquareIcon,
  },
  {
    name: 'Technical Analysis',
    description: 'Calculate and analyze technical indicators including SMA, EMA, RSI, MACD, and Bollinger Bands across all timeframes',
    icon: CpuChipIcon,
  },
  {
    name: 'Secure Infrastructure',
    description: 'Enterprise-grade security with API key authentication and rate limiting to protect your data',
    icon: LockClosedIcon,
  },
  {
    name: 'Developer Tools',
    description: 'Well-documented REST APIs with example code and SDKs for popular programming languages',
    icon: CodeBracketIcon,
  },
]

const stats = [
  { name: 'Time Intervals', value: '4+', icon: ClockIcon },
  { name: 'Technical Indicators', value: '50+', icon: ArrowTrendingUpIcon },
  { name: 'Years of Data', value: '10+', icon: ServerIcon },
  { name: 'Support', value: '24/7', icon: UserGroupIcon },
]

export const FeatureButtom: React.FC = () => {
  const { isDarkMode } = useTheme();

  return (
    <div className={`overflow-hidden py-16 sm:py-20 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl lg:text-center mb-12">
          <h2 className="text-base font-semibold leading-7 text-accent-primary">Financial Data API</h2>
          <p className={`mt-2 text-3xl font-bold tracking-tight sm:text-4xl ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
            Multi-Interval Market Data
          </p>
          <p className={`mt-6 text-lg leading-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Access comprehensive Indian stock market data with flexible time intervals. From 1-minute to daily data, built for developers, traders, and financial analysts.
          </p>
        </div>
        
        <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-12 sm:gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-2 lg:items-center">
          <div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {features.map((feature) => (
                <div key={feature.name} className={`p-6 rounded-lg shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-center gap-x-3 mb-3">
                    <feature.icon className="h-5 w-5 flex-none text-accent-primary" aria-hidden="true" />
                    <h3 className={`text-base font-semibold leading-7 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{feature.name}</h3>
                  </div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{feature.description}</p>
                </div>
              ))}
            </div>
            
            {/* Call to Action */}
            <div className="mt-10 flex items-center gap-x-6">
              <a
                href="#/docs"
                className={`doc-button ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
              >
                View Documentation
              </a>
              <a href="#/contact" className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300 hover:text-accent-primary' : 'text-gray-700 hover:text-accent-primary'}`}>
                Contact Support <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
          
          <div className="relative">
            <img
              alt="Stock market dashboard showing multiple timeframes"
              src="https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80"
              className="w-full max-w-none rounded-xl shadow-xl ring-1 ring-gray-200"
            />
            
            {/* Stats overlay */}
            <div className={`absolute -bottom-6 -right-6 p-6 rounded-lg shadow-xl ring-1 ${isDarkMode ? 'bg-gray-800 ring-gray-700' : 'bg-white ring-gray-200'}`}>
              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat) => (
                  <div key={stat.name} className="flex items-center gap-2">
                    <stat.icon className="h-5 w-5 text-accent-primary" />
                    <div>
                      <p className={`text-xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{stat.value}</p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{stat.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
